/**
 * Tool Combo Configuration Panel
 *
 * Split-view layout for managing tool combo templates:
 * - Left: List of templates
 * - Right: Template editor form (rendered by ToolComboTemplateEditor)
 *
 * Tool combos define which tools to use, in what order, and with what instructions.
 */

import React, { useState, useEffect, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Loading } from "@/components/common";
import {
  Save,
  RotateCcw,
  Search,
  ChevronRight,
  Check,
  Plus,
  Eye,
  Layers,
  X,
} from "lucide-react";
import {
  useToolComboConfig,
  useUpdateToolComboConfig,
  useToolEnhancementConfig,
} from "../hooks/useSystemConfig";
import { useToolsQuery } from "@/lib/queries";
import { useI18n } from "@/i18n";
import { systemApi } from "@/lib/api/system";
import type { ToolComboConfig, ToolComboTemplate } from "@/types/system";
import {
  ToolComboTemplateEditor,
  type ToolComboAvailableTool,
} from "./ToolComboTemplateEditor";

export const ToolComboPanel: React.FC = () => {
  const { messages, language } = useI18n();
  const t = messages.systemSettings?.toolCombo;
  const isChinese = language === "zh";

  const { data: toolsData, isLoading: toolsLoading } = useToolsQuery();
  const { data: enhancementConfig } = useToolEnhancementConfig();
  const {
    data: config,
    isLoading: configLoading,
    refetch,
  } = useToolComboConfig();
  const updateConfig = useUpdateToolComboConfig();

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [localConfig, setLocalConfig] = useState<
    Record<string, ToolComboTemplate>
  >({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newTemplateId, setNewTemplateId] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState("");

  const previewMutation = useMutation({
    mutationFn: async (templateId: string) => {
      const result = await systemApi.toolCombo.preview(
        templateId,
        isChinese ? "cn" : "en",
      );
      return result.context;
    },
    onSuccess: (content) => {
      setPreviewContent(content);
      setShowPreview(true);
    },
  });

  useEffect(() => {
    if (config?.templates) {
      setLocalConfig(config.templates);
      setHasChanges(false);
    }
  }, [config]);

  const availableTools = useMemo<ToolComboAvailableTool[]>(() => {
    if (!toolsData) return [];
    const enhancementTools = enhancementConfig?.tools || {};

    const tools: ToolComboAvailableTool[] = [];

    toolsData.builtin_toolkits?.forEach((toolkit) => {
      const toolName = toolkit.name.replace("builtin:", "");
      const enhancement = enhancementTools[toolName];
      tools.push({
        name: toolName,
        description: toolkit.description,
        category: toolkit.category || "other",
        type: "builtin",
        displayNameCn: enhancement?.display_name_cn,
        displayNameEn: enhancement?.display_name_en,
        descriptionCn: enhancement?.description_cn,
        descriptionEn: enhancement?.description_en,
      });
    });

    toolsData.custom_tools?.forEach((tool) => {
      const enhancement = enhancementTools[tool.name];
      tools.push({
        name: tool.name,
        description: tool.description,
        category: tool.category || "custom",
        type: "custom",
        displayNameCn: enhancement?.display_name_cn,
        displayNameEn: enhancement?.display_name_en,
        descriptionCn: enhancement?.description_cn,
        descriptionEn: enhancement?.description_en,
      });
    });

    return tools.sort((a, b) => a.name.localeCompare(b.name));
  }, [toolsData, enhancementConfig]);

  const filteredTemplates = useMemo(() => {
    const templates = Object.values(localConfig);
    if (!searchQuery) return templates;

    const query = searchQuery.toLowerCase();
    return templates.filter((tpl) => {
      const name = isChinese ? tpl.name_cn : tpl.name_en;
      return (
        tpl.id.toLowerCase().includes(query) ||
        name?.toLowerCase().includes(query) ||
        tpl.description_cn?.toLowerCase().includes(query) ||
        tpl.description_en?.toLowerCase().includes(query)
      );
    });
  }, [localConfig, searchQuery, isChinese]);

  const selectedTemplate = selectedTemplateId
    ? localConfig[selectedTemplateId]
    : null;

  const getDisplayName = (template: ToolComboTemplate): string => {
    if (isChinese) {
      return template.name_cn || template.name_en || template.id;
    }
    return template.name_en || template.name_cn || template.id;
  };

  const handleTemplateChange = (next: ToolComboTemplate) => {
    setLocalConfig((prev) => ({ ...prev, [next.id]: next }));
    setHasChanges(true);
  };

  const handleCreateTemplate = () => {
    if (!newTemplateId.trim()) return;

    const id = newTemplateId.trim();
    if (localConfig[id]) {
      alert(isChinese ? "模板 ID 已存在" : "Template ID already exists");
      return;
    }

    const newTemplate: ToolComboTemplate = {
      id,
      name_cn: "",
      name_en: "",
      description_cn: "",
      description_en: "",
      tools: [],
      guidance: "",
      enabled: true,
    };

    setLocalConfig((prev) => ({ ...prev, [id]: newTemplate }));
    setSelectedTemplateId(id);
    setIsCreating(false);
    setNewTemplateId("");
    setHasChanges(true);
  };

  const handleDeleteTemplate = (templateId: string) => {
    if (
      !confirm(
        t?.confirmDelete || "Are you sure you want to delete this template?",
      )
    )
      return;

    setLocalConfig((prev) => {
      const newConfig = { ...prev };
      delete newConfig[templateId];
      return newConfig;
    });

    if (selectedTemplateId === templateId) {
      setSelectedTemplateId(null);
    }
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      const newConfig: ToolComboConfig = { templates: localConfig };
      await updateConfig.mutateAsync(newConfig);
      setHasChanges(false);
      alert(t?.saveSuccess || "Saved successfully");
    } catch (error) {
      alert(`${t?.saveFailed || "Save failed"}: ${error}`);
    }
  };

  const handleReload = () => {
    refetch();
  };

  const handlePreview = () => {
    if (!selectedTemplateId) return;
    if (hasChanges) {
      alert(
        isChinese
          ? "请先保存更改再预览"
          : "Please save changes before previewing",
      );
      return;
    }
    previewMutation.mutate(selectedTemplateId);
  };

  if (toolsLoading || configLoading) {
    return <Loading message={t?.loading || "Loading..."} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Layers className="w-5 h-5" />
            {t?.title || "Tool Combos"}
          </h2>
          <p className="text-sm text-gray-500">
            {t?.description || "Configure tool combination templates"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={handleReload}
            disabled={updateConfig.isPending}
          >
            <RotateCcw className="w-4 h-4 mr-1" />
            {t?.reload || "Reload"}
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!hasChanges || updateConfig.isPending}
          >
            <Save className="w-4 h-4 mr-1" />
            {updateConfig.isPending
              ? t?.saving || "Saving..."
              : t?.save || "Save"}
          </Button>
        </div>
      </div>

      <div className="flex gap-4 h-[calc(100vh-280px)] min-h-[500px]">
        <Card className="w-1/3 flex flex-col overflow-hidden">
          <div className="p-3 border-b space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={t?.searchPlaceholder || "Search templates..."}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
            {isCreating ? (
              <div className="flex gap-2">
                <Input
                  placeholder={
                    t?.templateIdPlaceholder ||
                    "Template ID (e.g., astro_basic)"
                  }
                  value={newTemplateId}
                  onChange={(e) => setNewTemplateId(e.target.value)}
                  className="h-8 text-sm"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleCreateTemplate();
                    if (e.key === "Escape") {
                      setIsCreating(false);
                      setNewTemplateId("");
                    }
                  }}
                />
                <Button
                  size="sm"
                  onClick={handleCreateTemplate}
                  className="h-8"
                >
                  <Check className="w-4 h-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setIsCreating(false);
                    setNewTemplateId("");
                  }}
                  className="h-8"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                className="w-full"
                onClick={() => setIsCreating(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                {t?.newTemplate || "New Template"}
              </Button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {filteredTemplates.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                {t?.noTemplateSelected || "Select a template"}
              </div>
            ) : (
              filteredTemplates.map((template) => (
                <div
                  key={template.id}
                  onClick={() => setSelectedTemplateId(template.id)}
                  className={`px-3 py-2 cursor-pointer border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                    selectedTemplateId === template.id
                      ? "bg-blue-50 border-l-2 border-l-blue-500"
                      : ""
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Layers className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-900 truncate">
                        {getDisplayName(template)}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {template.enabled !== false && (
                        <Check className="w-4 h-4 text-green-500" />
                      )}
                      <span className="text-xs text-gray-400">
                        {template.tools.length}
                      </span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 truncate mt-0.5 ml-6 font-mono">
                    {template.id}
                  </p>
                </div>
              ))
            )}
          </div>

          <div className="p-2 border-t bg-gray-50 text-xs text-gray-500">
            {(t?.templateCount || "{count} template(s)").replace(
              "{count}",
              String(Object.keys(localConfig).length),
            )}
          </div>
        </Card>

        <Card className="flex-1 overflow-y-auto">
          {selectedTemplate ? (
            <ToolComboTemplateEditor
              template={selectedTemplate}
              availableTools={availableTools}
              isChinese={isChinese}
              t={t}
              onChange={handleTemplateChange}
              onPreview={handlePreview}
              previewPending={previewMutation.isPending}
              previewDisabledReason={
                hasChanges ? (isChinese ? "请先保存" : "Save first") : undefined
              }
              onDelete={() => handleDeleteTemplate(selectedTemplate.id)}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-500">
              <div className="text-center">
                <Layers className="w-12 h-12 mx-auto mb-2 opacity-20" />
                <p>{t?.noTemplateSelected || "Select a template"}</p>
              </div>
            </div>
          )}
        </Card>
      </div>

      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-600" />
                {t?.previewPrompt || "Preview Prompt"}
              </h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <pre className="text-sm text-gray-800 bg-gray-100 rounded-lg p-4 whitespace-pre-wrap font-mono">
                {previewContent}
              </pre>
            </div>
            <div className="flex items-center justify-end px-4 py-3 border-t bg-gray-50">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowPreview(false)}
              >
                {t?.closePreview || "Close"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
